import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Ticket, Loader2, QrCode,
  CheckCircle, Share2, ExternalLink, Smartphone,
  IdCard, Receipt,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Single ticket — clean editorial layout mirroring the brand reference:
 * column-based grid (EVENT | DATE) on top, thin horizontal divider, then
 * (TIME | VENUE | TICKET TYPE) underneath. Pink-uppercase labels, heavy
 * black uppercase values. A pink→cyan dashed gradient tear-line with
 * half-circle hole-punches separates the stub from the QR strip. The
 * lower half (QR + Ticket ID + IMPORTANT INFO with icon rows) reveals
 * when the user taps the ticket.
 *
 * The half-punches fill matches the wallet's interior color so they read
 * as cut-outs from the ticket against the casing.
 */
const InfoRow = ({ Icon, title, desc }) => (
  <div className="flex items-center gap-3 py-2.5">
    <div className="w-9 h-9 rounded-full p-[1.5px] bg-gradient-to-br from-fuchsia-500 to-cyan-500 flex-shrink-0">
      <div className="w-full h-full rounded-full bg-zinc-900 flex items-center justify-center">
        <Icon className="w-4 h-4 text-cyan-300" strokeWidth={2} />
      </div>
    </div>
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-white leading-none">{title}</p>
      <p className="text-[11px] text-white/65 leading-snug mt-1">{desc}</p>
    </div>
  </div>
);

const TicketSlot = ({ ticket, idx, isOpen, onToggle, onShare, onFullView }) => {
  const price = ticket.price_pence === 0
    ? 'Free'
    : `£${(ticket.price_pence / 100).toFixed(2)}`;
  const dateObj = ticket.event_date ? new Date(ticket.event_date) : null;
  const dateLabel = dateObj
    ? dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase()
    : 'TBA';
  const dayLabel = dateObj
    ? dateObj.toLocaleDateString('en-GB', { weekday: 'long' }).toUpperCase()
    : '';
  const timeLabel = dateObj
    ? dateObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : 'TBA';

  // Inner wallet bg colour — used to "cut" the half-circle punches so they
  // read as transparent against the casing.
  const punchColor = '#0a0a0a';
  const gradId = `tear-${ticket.ticket_id}`;

  return (
    <div
      className="relative rounded-b-2xl bg-white shadow-[0_10px_24px_-10px_rgba(0,0,0,0.7)] border border-zinc-200 overflow-visible"
      data-testid={`ticket-card-${ticket.ticket_id}`}
    >
      {/* ===== TICKET STUB (always visible) ===== */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left overflow-hidden block"
      >
        {/* Square-notched TOP edge of the violet band — small upward
            rectangles emerge from the band, mirroring the reference's
            "ticket-perforation" tab look. */}
        <svg
          aria-hidden
          className="block w-full -mb-px"
          viewBox="0 0 100 3"
          preserveAspectRatio="none"
          style={{ height: '7px' }}
        >
          {Array.from({ length: 11 }, (_, i) => {
            const step = 100 / 11;
            return (
              <rect
                key={i}
                x={i * step + step * 0.28}
                y={0}
                width={step * 0.44}
                height={3}
                fill="#5b21b6"
              />
            );
          })}
        </svg>

        {/* ===== PURPLE BRANDED HEADER ===== */}
        <div
          className="relative overflow-hidden text-white"
          style={{
            background:
              'linear-gradient(135deg, #5b21b6 0%, #6d28d9 50%, #4c1d95 100%)',
          }}
        >
          {/* Decorative soft purple blobs in the bottom-right corner */}
          <div
            aria-hidden
            className="pointer-events-none absolute -right-6 -bottom-10 w-36 h-36 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.45), rgba(167,139,250,0) 70%)' }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute right-3 bottom-2 w-20 h-20 rounded-full border-[14px] border-violet-300/20"
          />

          <div className="relative px-5 pt-4 pb-5">
            <div className="flex items-start justify-between gap-3">
              <span className="text-white text-2xl font-extrabold tracking-tight leading-none">
                RollM8s
              </span>
              <span className="text-white/85 text-[10px] tracking-[0.22em] font-semibold uppercase mt-1.5">
                Ticket #{idx + 1}
              </span>
            </div>
            <div className="mt-3">
              <p className="text-white text-[15px] font-extrabold uppercase tracking-tight leading-tight">
                Good Times.
              </p>
              <p className="text-white text-[15px] font-extrabold uppercase tracking-tight leading-tight">
                Anytime. Anywhere.
              </p>
            </div>
          </div>

          {/* Scalloped bottom edge — small downward semicircles emerging from
              the purple band into the white ticket area. */}
          <svg
            aria-hidden
            className="block w-full -mb-px"
            viewBox="0 0 100 3"
            preserveAspectRatio="none"
            style={{ height: '7px' }}
          >
            {Array.from({ length: 26 }, (_, i) => (
              <circle key={i} cx={i * (100 / 26) + (100 / 52)} cy="0" r={100 / 52} fill="#5b21b6" />
            ))}
          </svg>
        </div>

        <div className="px-5 pt-4 pb-3">
          {/* Row 1 — EVENT | DATE */}
          <div className="grid grid-cols-[1fr_1px_auto] gap-x-4">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700">Event</p>
              <p className="text-[15px] font-extrabold uppercase tracking-tight text-zinc-900 leading-tight mt-1 truncate">
                {ticket.event_name}
              </p>
              <p className="text-[11px] text-zinc-500 mt-1">
                {price}
                {ticket.payment_type === 'cash' && <span className="ml-1 text-amber-600">(Cash at door)</span>}
              </p>
            </div>
            <div className="bg-zinc-200" />
            <div className="min-w-[96px]">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700">Date</p>
              <p className="text-[13px] font-extrabold uppercase tracking-tight text-zinc-900 leading-tight mt-1 whitespace-nowrap">
                {dateLabel}
              </p>
              {dayLabel && <p className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">{dayLabel}</p>}
            </div>
          </div>

          {/* Thin horizontal divider */}
          <div className="border-t border-zinc-200 my-3" />

          {/* Row 2 — TIME | VENUE | TYPE */}
          <div className="grid grid-cols-[auto_1px_1fr_1px_auto] gap-x-3 items-start">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700">Time</p>
              <p className="text-[13px] font-extrabold uppercase tracking-tight text-zinc-900 leading-tight mt-1">{timeLabel}</p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">Onwards</p>
            </div>
            <div className="bg-zinc-200" />
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700">Venue</p>
              <p className="text-[13px] font-extrabold uppercase tracking-tight text-zinc-900 leading-tight mt-1 truncate">
                {ticket.event_location || 'TBA'}
              </p>
            </div>
            <div className="bg-zinc-200" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-700">Type</p>
              <p className="text-[13px] font-extrabold uppercase tracking-tight text-zinc-900 leading-tight mt-1">General</p>
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">Admission</p>
            </div>
          </div>

          {/* Status badge + Tap to scan pill */}
          <div className="flex items-center justify-between gap-2 mt-4">
            {ticket.checked_in ? (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-[10px] px-2 py-0">
                <CheckCircle className="w-3 h-3 mr-0.5" />Checked In
              </Badge>
            ) : (
              <Badge variant="outline" className="border-zinc-300 text-zinc-700 text-[10px] px-2 py-0 uppercase tracking-wider">Valid</Badge>
            )}
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold tracking-wide shadow-md transition-all ${
                isOpen
                  ? 'bg-zinc-900 text-white'
                  : 'bg-gradient-to-r from-fuchsia-500 via-purple-500 to-cyan-500 text-white animate-pulse-soft'
              }`}
              style={isOpen ? undefined : { boxShadow: '0 0 14px rgba(217,70,239,0.45)' }}
            >
              <QrCode className="w-3.5 h-3.5" />
              {isOpen ? 'Close ticket' : 'Tap to scan'}
            </span>
          </div>
        </div>
      </button>

      {/* ===== PERFORATED TEAR LINE — pink→cyan dashed gradient, with half-hole punches ===== */}
      <div className="relative h-6">
        <div
          aria-hidden
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full"
          style={{ backgroundColor: punchColor }}
        />
        <div
          aria-hidden
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-6 rounded-full"
          style={{ backgroundColor: punchColor }}
        />
        <svg
          aria-hidden
          className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-[2px] w-[calc(100%-3rem)]"
          preserveAspectRatio="none"
          viewBox="0 0 100 1"
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#d946ef" />
              <stop offset="100%" stopColor="#06b6d4" />
            </linearGradient>
          </defs>
          <line x1="0" y1="0.5" x2="100" y2="0.5" stroke={`url(#${gradId})`} strokeWidth="1.5" strokeDasharray="2.2 1.6" />
        </svg>
      </div>

      {/* ===== LOWER STUB (collapsible) ===== */}
      <div
        className="grid transition-[grid-template-rows] duration-500 ease-out"
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-5 pt-4 pb-4 space-y-4">
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.22em] text-violet-700 font-bold">Ticket ID</p>
              <p className="text-[12px] text-zinc-900 font-extrabold tracking-wider uppercase break-all mt-1">{ticket.ticket_id}</p>
            </div>

            <div className="flex justify-center">
              {ticket.qr_code && (
                <img
                  loading="lazy"
                  src={`data:image/png;base64,${ticket.qr_code}`}
                  alt="Ticket QR Code"
                  className="w-44 h-44 rounded-xl bg-white p-2 border border-zinc-200"
                  data-testid={`ticket-qr-${ticket.ticket_id}`}
                />
              )}
            </div>
            <p className="text-[11px] text-center text-zinc-600">Scan at entry for access</p>

            {/* IMPORTANT INFO — black panel, gradient header text, icon rows */}
            <div className="rounded-xl bg-zinc-900 text-zinc-100 px-4 py-3" data-testid={`single-use-${ticket.ticket_id}`}>
              <p className="text-[11px] uppercase tracking-[0.22em] font-extrabold bg-gradient-to-r from-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">
                Important Info
              </p>
              <div className="divide-y divide-white/10 mt-1">
                <InfoRow Icon={Ticket} title="Entry" desc="Arrive early to avoid lines." />
                <InfoRow Icon={IdCard} title="Valid ID" desc="For 18+ events bring a valid photo ID." />
                <InfoRow Icon={Receipt} title="Refunds" desc="Check the organiser's terms and conditions." />
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                onClick={(e) => { e.stopPropagation(); onShare(ticket); }}
                data-testid={`share-ticket-${ticket.ticket_id}`}
              >
                <Share2 className="w-3.5 h-3.5 mr-1.5" />Share
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                onClick={(e) => { e.stopPropagation(); onFullView(ticket); }}
                data-testid={`view-ticket-${ticket.ticket_id}`}
              >
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />Full View
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MyTicketsPage = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTicket, setExpandedTicket] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        const res = await axios.get(`${API}/tickets/my-tickets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTickets(res.data);
      } catch { /* empty */ } finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleShareTicket = async (ticket) => {
    const url = `${window.location.origin}/tickets/view/${ticket.ticket_id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Ticket for ${ticket.event_name}`,
          text: `My ticket for ${ticket.event_name} on RollM8s`,
          url,
        });
      } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Ticket link copied!');
    }
  };

  // ===== SHAKE-TO-FLASH QR =====
  // A wrist-flick of the phone snaps the next un-scanned ticket open so users
  // don't have to fumble at the door with hands full. Uses DeviceMotion for
  // shake detection, navigator.vibrate for haptic confirmation, and respects
  // iOS 13+ permission flow (requires a user gesture before listening).
  useEffect(() => {
    if (tickets.length === 0) return undefined;

    const SHAKE_THRESHOLD = 22; // m/s² delta — needs a deliberate flick
    const COOLDOWN_MS = 1800;
    let last = null;
    let lastShakeAt = 0;

    const onMotion = (e) => {
      const acc = e.accelerationIncludingGravity || e.acceleration;
      if (!acc || acc.x == null) return;
      if (!last) {
        last = { x: acc.x, y: acc.y, z: acc.z };
        return;
      }
      const dx = acc.x - last.x;
      const dy = acc.y - last.y;
      const dz = acc.z - last.z;
      const delta = Math.sqrt(dx * dx + dy * dy + dz * dz);
      last = { x: acc.x, y: acc.y, z: acc.z };

      const now = Date.now();
      if (delta > SHAKE_THRESHOLD && now - lastShakeAt > COOLDOWN_MS) {
        lastShakeAt = now;
        const next = tickets.find((t) => !t.checked_in) || tickets[0];
        if (!next) return;
        setExpandedTicket(next.ticket_id);
        if (navigator.vibrate) navigator.vibrate([40, 60, 40]);
        toast.success(`🎟 ${next.event_name} — ready to scan`);
        // Smoothly scroll the opened ticket into view
        requestAnimationFrame(() => {
          const el = document.querySelector(`[data-testid="ticket-card-${next.ticket_id}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    };

    const attach = () => window.addEventListener('devicemotion', onMotion);
    const detach = () => window.removeEventListener('devicemotion', onMotion);

    const needsPermission = typeof DeviceMotionEvent !== 'undefined'
      && typeof DeviceMotionEvent.requestPermission === 'function';

    let cancelGesture = () => {};
    if (needsPermission) {
      // iOS — wait for any user tap, then request permission once.
      const requestOnce = async () => {
        try {
          const res = await DeviceMotionEvent.requestPermission();
          if (res === 'granted') attach();
        } catch { /* user denied */ }
        window.removeEventListener('touchend', requestOnce);
        window.removeEventListener('click', requestOnce);
      };
      window.addEventListener('touchend', requestOnce, { once: true });
      window.addEventListener('click', requestOnce, { once: true });
      cancelGesture = () => {
        window.removeEventListener('touchend', requestOnce);
        window.removeEventListener('click', requestOnce);
      };
    } else if (typeof window !== 'undefined' && 'DeviceMotionEvent' in window) {
      attach();
    }

    return () => { cancelGesture(); detach(); };
  }, [tickets]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isEmpty = tickets.length === 0;

  return (
    <div className="min-h-screen bg-black">
      <Navbar activePage="my-tickets" />

      {/* Shake hint — floats at the top right, no redundant back button
          (the Navbar already carries primary navigation). */}
      {!isEmpty && (
        <div className="px-3 pt-2 pb-1 flex justify-end max-w-xl mx-auto">
          <div
            className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-2.5 py-1 text-[10px] text-white/70 backdrop-blur-sm"
            data-testid="shake-hint"
            title="Give your phone a quick shake to pop your next ticket open"
          >
            <Smartphone className="w-3 h-3 text-fuchsia-400" />
            <span className="tracking-wide">Shake to flash QR</span>
          </div>
        </div>
      )}

      {/* THE WALLET CASE — leather artwork sliced into 3 parts (top, repeatable
          middle leather strip, bottom) so the pouch literally grows taller as
          more tickets are added. Sized to fill the viewport edge-to-edge on
          mobile and stretch comfortably on tablet/desktop. */}
      <div className="max-w-3xl mx-auto px-3 sm:px-5 pb-10">
        <div className="relative w-full" data-testid="wallet-case">
          {/* Fixed top — leather tab + ROLLM8S header + tagline + start of white window */}
          <img
            src="/wallet-top.jpg"
            alt=""
            className="block w-full select-none -mb-px"
            draggable="false"
          />

          {/* Stretchable middle — leather sides repeat vertically, tickets sit
              in the white window between the sides. */}
          <div
            className="relative"
            style={{
              backgroundImage: 'url(/wallet-middle-slice.jpg)',
              backgroundRepeat: 'repeat-y',
              backgroundSize: '100% auto',
              backgroundPosition: 'center top',
            }}
          >
            <div
              className="space-y-2"
              style={{
                paddingLeft: '14.5%',
                paddingRight: '14.5%',
                paddingTop: '4px',
                paddingBottom: '4px',
              }}
              data-testid="wallet-interior"
            >
              {isEmpty ? (
                <div className="py-10 text-center space-y-3">
                  <Ticket className="w-10 h-10 text-zinc-400 mx-auto" />
                  <h2 className="text-sm font-bold text-zinc-700">Your wallet is empty</h2>
                  <p className="text-xs text-zinc-500">Browse events to grab your first ticket</p>
                  <Button
                    size="sm"
                    className="rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white hover:opacity-90"
                    onClick={() => navigate('/events')}
                    data-testid="browse-events-btn"
                  >
                    Browse Events
                  </Button>
                </div>
              ) : (
                tickets.map((ticket, idx) => (
                  <TicketSlot
                    key={ticket.ticket_id}
                    ticket={ticket}
                    idx={idx}
                    isOpen={expandedTicket === ticket.ticket_id}
                    onToggle={() => setExpandedTicket(
                      expandedTicket === ticket.ticket_id ? null : ticket.ticket_id,
                    )}
                    onShare={handleShareTicket}
                    onFullView={(t) => navigate(`/tickets/view/${t.ticket_id}`)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Fixed bottom — gradient sash + debossed ROLLM8S footer */}
          <img
            src="/wallet-bottom.jpg"
            alt=""
            className="block w-full select-none -mt-px"
            draggable="false"
          />
        </div>
      </div>
    </div>
  );
};

export default MyTicketsPage;
